import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ProcessSlider — full-width single-image carousel with labelled dot navigation.
 *
 * One slide visible at a time, large hero-style image. Below the slide,
 * 7 labelled dots (number + short label) act as a stepper / pagination.
 *
 * Props:
 *   steps: Array<{ id, num, title, subtitle, image, alt, dotLabel? }>
 *   autoplayMs: number (default 5000, 0 disables)
 */
const ProcessSlider = ({ steps, autoplayMs = 5000 }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const scrollTo = useCallback((index) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(steps.length - 1, index));
    const child = track.children[clamped];
    if (!child) return;
    track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    setActive(clamped);
  }, [steps.length]);

  // Sync active dot with swipe scroll
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const trackLeft = track.scrollLeft;
        let closestIdx = 0;
        let closestDelta = Infinity;
        for (let i = 0; i < track.children.length; i++) {
          const c = track.children[i];
          const delta = Math.abs(c.offsetLeft - track.offsetLeft - trackLeft);
          if (delta < closestDelta) {
            closestDelta = delta;
            closestIdx = i;
          }
        }
        setActive(closestIdx);
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      track.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Autoplay
  useEffect(() => {
    if (!autoplayMs || paused) return;
    timer.current = setInterval(() => {
      setActive((prev) => {
        const next = (prev + 1) % steps.length;
        const track = trackRef.current;
        if (track) {
          const child = track.children[next];
          if (child) {
            track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: 'smooth' });
          }
        }
        return next;
      });
    }, autoplayMs);
    return () => clearInterval(timer.current);
  }, [autoplayMs, paused, steps.length]);

  // Keyboard
  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') scrollTo(active - 1);
    if (e.key === 'ArrowRight') scrollTo(active + 1);
  };

  return (
    <div
      className="relative w-full"
      role="group"
      aria-roledescription="carousel"
      aria-label="Notre processus de prise en charge"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onKeyDown={onKeyDown}
      tabIndex={0}
      data-testid="process-slider"
    >
      {/* Track — one slide at a time, full width */}
      <ul
        ref={trackRef}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        aria-live="polite"
      >
        {steps.map((step, idx) => (
          <li
            key={step.id}
            className="snap-center shrink-0 w-full"
            aria-roledescription="slide"
            aria-label={`${idx + 1} sur ${steps.length} — ${step.title}`}
            data-testid={`process-slide-${idx}`}
          >
            <article className="relative bg-gray-100 rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl mx-auto max-w-5xl">
              {/* Large image */}
              <div className="aspect-[16/10] sm:aspect-[16/9] overflow-hidden bg-gray-200 relative">
                <img
                  src={step.image}
                  alt={step.alt}
                  className="w-full h-full object-cover"
                  loading={idx < 2 ? 'eager' : 'lazy'}
                  decoding="async"
                />
                {/* Top-left number badge */}
                <div className="absolute top-4 left-4 sm:top-6 sm:left-6">
                  <span className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm text-gray-900 pl-2 pr-4 py-1.5 rounded-full shadow-lg ring-1 ring-gray-200">
                    <span className="w-7 h-7 rounded-full bg-[#52a675] text-white text-xs font-bold flex items-center justify-center">
                      {step.num}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide">Étape {step.num}</span>
                  </span>
                </div>
                {/* Bottom gradient + caption overlay */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-5 sm:p-8 pt-16 sm:pt-24">
                  <h3 className="text-white text-xl sm:text-3xl font-bold leading-tight max-w-3xl">
                    {step.title}
                  </h3>
                  <p className="text-white/85 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
                    {step.subtitle}
                  </p>
                </div>
              </div>
            </article>
          </li>
        ))}
      </ul>

      {/* Side arrows */}
      <button
        type="button"
        onClick={() => scrollTo(active - 1)}
        aria-label="Étape précédente"
        className="hidden sm:flex absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white text-gray-900 shadow-xl hover:bg-gray-50 hover:scale-110 transition-all items-center justify-center z-10 ring-1 ring-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
        disabled={active === 0}
        data-testid="process-prev"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        type="button"
        onClick={() => scrollTo(active + 1)}
        aria-label="Étape suivante"
        className="hidden sm:flex absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white text-gray-900 shadow-xl hover:bg-gray-50 hover:scale-110 transition-all items-center justify-center z-10 ring-1 ring-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
        disabled={active === steps.length - 1}
        data-testid="process-next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Labelled stepper dots — visible numbers + labels under each step */}
      <div
        className="mt-8 sm:mt-10 flex items-stretch justify-between gap-2 sm:gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0"
        role="tablist"
        aria-label="Navigation entre les étapes"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {steps.map((step, idx) => {
          const isActive = idx === active;
          return (
            <button
              key={step.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={`Aller à l'étape ${idx + 1} — ${step.title}`}
              onClick={() => scrollTo(idx)}
              className={
                "group flex flex-col items-center text-center gap-2 shrink-0 sm:flex-1 px-2 sm:px-3 py-3 rounded-xl transition-all duration-300 " +
                (isActive
                  ? "bg-[#52a675]/10 ring-1 ring-[#52a675]/30"
                  : "hover:bg-gray-100")
              }
              data-testid={`process-dot-${idx}`}
            >
              <span
                className={
                  "w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all " +
                  (isActive
                    ? "bg-[#52a675] text-white shadow-md scale-110"
                    : "bg-gray-200 text-gray-600 group-hover:bg-gray-300")
                }
              >
                {step.num}
              </span>
              <span
                className={
                  "text-[11px] sm:text-xs leading-tight whitespace-nowrap sm:whitespace-normal max-w-[5rem] sm:max-w-none font-medium transition-colors " +
                  (isActive ? "text-[#3d8a5c]" : "text-gray-500 group-hover:text-gray-800")
                }
              >
                {step.dotLabel || step.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ProcessSlider;
